import { useI18n } from 'vue-i18n'

export const securityMessages = {
  en: {
    address_code_injection: 'Address-header code injection',
    invalid_address_control_characters: 'Invalid address control characters',
    invalid_archive: 'Invalid archive',
    encrypted_or_invalid_archive: 'Encrypted or invalid archive',
    unreadable_archive_entry: 'Unreadable archive entry',
    message_size_limit: 'Message size limit exceeded',
    attachment_count_limit: 'Attachment count exceeded',
    mime_depth_limit: 'MIME depth exceeded',
    part_size_limit: 'MIME part size exceeded',
    missing_mime_payload: 'Missing MIME payload',
    missing_part_content: 'Missing MIME part content',
    invalid_attachment_filename: 'Invalid attachment filename',
    discardChanges: 'Discard unsaved changes and leave?',
    discard: 'Discard changes',
    channel: 'Channel',
    channel_id: 'Channel ID',
    platform: 'Platform',
    ruleMissing: 'The rule or Channel does not exist. Reload the current list.',
    create: 'New rule',
    newRule: 'New Security Rule',
    selectChannels: 'Select Channels',
    removeChannel: 'Remove unavailable Channel',
    loadMore: 'Load more Channels',
    retryChannels: 'Reload Channel ownership',
    channelHelp:
      'Select 1–100 Channels. Each Channel can use one rule. Unassigned Channels use server defaults.',
    removedHelp: 'Removed Channels will return to server defaults:',
    channelConflict:
      'One or more selected Channels are unavailable or belong to another rule. Review ownership and reselect before saving.',
    channelInvalid: 'Select 1–100 distinct positive numeric Channel IDs.',
    optionsMismatch: 'Loaded options do not match the selected Channel. Reload before saving.',
    today: 'Today',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    security: 'Security',
    config: 'Config',
    records: 'Records',
    loading: 'Loading…',
    retry: 'Retry',
    failed: 'Request failed. Please retry.',
    denied: 'You do not have permission to view this page.',
    edit: 'Edit',
    delete: 'Delete',
    deleteRule: 'Delete security rule',
    deleteRuleConfirm:
      'Delete security rule #{id}? Channels: {channels}. These Channels will immediately return to server default security config.',
    deleteRecord: 'Delete security record',
    deleteRecordConfirm: 'Permanently delete security record #{id}? This action cannot be undone.',
    editRule: 'Edit security rule',
    appliesTo: 'Applies to Channels',
    details: 'Details',
    actions: 'Actions',
    view: 'View',
    recordInfo: 'Record information',
    review: 'Review',
    save: 'Save',
    close: 'Close',
    updated_at: 'Updated at',
    createdTime: 'Created Time',
    updatedTime: 'Updated Time',
    updated_by: 'Updated by (ID)',
    version: 'Version',
    limits: 'Structural limits',
    rule_modes: 'Risk handling',
    range: 'Allowed range',
    policyHelp:
      'Structural limits always block. Observe records risk and continues normal processing, including ticket creation and existing rules. Saved config applies to later checks only; historical mail is not reprocessed.',
    created_at: 'Time',
    block: 'Block',
    preview: 'Observe',
    blocked: 'Blocked',
    previewed: 'Observation record',
    pending: 'Pending review',
    reviewed: 'Reviewed',
    false_positive: 'Marked false positive',
    released: 'Manually released',
    external_message_id: 'Gmail message ID',
    release: 'Release',
    releaseRecord: 'Release blocked email #{id}',
    release_reason: 'Release reason',
    releaseHelp:
      'Required, up to 500 characters. This releases only this issue, and the review decision cannot be changed afterwards. Other issues on the same email need their own release.',
    resubmitSync: 'Resubmit sync',
    findings: 'Findings',
    review_remark: 'Review remark',
    reviewed_by: 'Reviewer ID',
    reviewed_at: 'Reviewed at',
    search: 'Search',
    reset: 'Reset',
    dates: 'Date range',
    dateHelp:
      'Full selected days in your local timezone; maximum 90 days. Displayed timestamps use your browser timezone.',
    keyword: 'Sender / subject',
    keywordHelp: 'Sender email or subject prefix (3–320 characters).',
    all: 'All',
    action: 'Action',
    status: 'Review',
    risk_code: 'Risk code',
    subject: 'Subject',
    from_address: 'Sender',
    id: 'ID',
    total: 'Issue records',
    statsHelp:
      'Counts issue records in the selected detection-time range. One email can have several issue records, and repeated detection of the same issue keeps its original record.',
    by_action: 'By action',
    by_status: 'By review status',
    by_risk_code: 'By primary risk',
    filename: 'Filename',
    description: 'Description',
    field: 'Field',
    type: 'Type',
    code: 'Code',
    blocking: 'Blocks processing',
    yes: 'Yes',
    no: 'No',
    limitInvalid: 'Every limit must be an integer within the server-provided bounds.',
    attachmentInvalid: 'Attachment size cannot exceed message size.',
    modeInvalid: 'Only block and observe are supported.',
    dateInvalid: 'Select a valid date range, in order, spanning at most 90 days.',
    keywordInvalid: 'Keyword must contain 3–320 characters.',
    riskInvalid: 'Risk code must contain 1–64 lowercase letters or underscores.',
    statusInvalid: 'Select a valid review status.',
    remarkInvalid: 'Remark cannot exceed 500 characters.',
    reasonRequired: 'Enter a release reason.',
    reasonInvalid: 'Release reason cannot exceed 500 characters.',
    max_message_bytes: 'Message size limit',
    max_attachment_bytes: 'Attachment size limit',
    max_attachments: 'Attachment count limit',
    max_mime_depth: 'MIME nesting limit',
    max_archive_depth: 'Archive nesting limit',
    max_expanded_bytes: 'Total expanded size limit',
    header_injection: 'Address-header code injection',
    header_format: 'Malformed address headers',
    dangerous_attachment: 'Dangerous attachment',
    encrypted_attachment: 'Encrypted attachment',
    uninspectable_archive: 'Archive cannot be fully inspected',
  },
}

export function useSecurityI18n() {
  const { t, te } = useI18n()
  const text = (key: string | null | undefined, params?: Record<string, string | number>) =>
    key
      ? te(`security.${key}`, 'en')
        ? params
          ? t(`security.${key}`, params)
          : t(`security.${key}`)
        : key
      : '—'
  return { text }
}
